import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import { format } from 'date-fns/format';
import { fr } from 'date-fns/locale/fr';

export type PageHeaderProps = {
  /** Main page title, rendered with the V2 serif heading. */
  title: React.ReactNode;
  /** Optional supporting description shown under the title. */
  subtitle?: React.ReactNode;
  /**
   * Refresh handler. When provided, the header shows the "Mis à jour …" label
   * and a sober refresh button. Clicking it calls this handler and, once it
   * resolves, refreshes the displayed update time.
   */
  onRefresh?: () => void | Promise<void>;
  /**
   * Controlled "last updated" timestamp. When omitted, the header tracks it
   * internally (set on mount, then after every successful refresh).
   */
  lastUpdatedAt?: Date | null;
  /** Controlled refreshing state. When omitted, it is tracked internally. */
  refreshing?: boolean;
  /** Extra actions rendered on the right, after the refresh control. */
  actions?: React.ReactNode;
  /**
   * Custom content rendered full-width *under* the title/refresh bar. Use it for
   * page-specific header controls (filters, period selectors, tabs…).
   */
  children?: React.ReactNode;
  /** Refresh button label (default "Actualiser"). */
  refreshLabel?: string;
  /** Prefix shown before the update time (default "Mis à jour"). */
  lastUpdatedPrefix?: string;
  id?: string;
  className?: string;
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Human, French-first "last updated" label, e.g. `aujourd'hui, 09:42`,
 * `hier, 09:42` or `12 juin, 09:42` (the year is added only when it differs
 * from the current one).
 */
export const formatLastUpdated = (date: Date, now: Date = new Date()): string => {
  const time = format(date, 'HH:mm', { locale: fr });
  if (isSameDay(date, now)) {
    return `aujourd'hui, ${time}`;
  }
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (isSameDay(date, yesterday)) {
    return `hier, ${time}`;
  }
  const pattern = date.getFullYear() === now.getFullYear() ? 'd MMMM' : 'd MMMM yyyy';
  return `${format(date, pattern, { locale: fr })}, ${time}`;
};

const PageHeader = ({
  title,
  subtitle = ' ',
  onRefresh,
  lastUpdatedAt,
  refreshing,
  actions,
  children,
  refreshLabel = 'Actualiser',
  lastUpdatedPrefix = 'Mis à jour',
  id,
  className,
}: PageHeaderProps) => {
  // `lastUpdatedAt` switches the date to controlled mode (even when explicitly null).
  const isControlledDate = lastUpdatedAt !== undefined;
  const [internalLastUpdated, setInternalLastUpdated] = useState<Date>(() => new Date());
  const [internalRefreshing, setInternalRefreshing] = useState(false);

  // Guard against state updates after unmount, the refresh handler being async.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isRefreshing = refreshing ?? internalRefreshing;
  const displayedDate = isControlledDate ? lastUpdatedAt : internalLastUpdated;

  const handleRefresh = useCallback(async () => {
    if (!onRefresh || internalRefreshing) {
      return;
    }
    setInternalRefreshing(true);
    try {
      await onRefresh();
      // Advance the clock only on success; in controlled mode the parent owns it.
      if (!isControlledDate && mountedRef.current) {
        setInternalLastUpdated(new Date());
      }
    } catch {
      // Swallowed on purpose: surfacing the failure is the handler's responsibility.
      // We still stop the spinner below so the button stays usable.
    } finally {
      if (mountedRef.current) {
        setInternalRefreshing(false);
      }
    }
  }, [onRefresh, internalRefreshing, isControlledDate]);

  const hasRefresh = Boolean(onRefresh);

  return (
    <Root id={id} className={className}>
      <Bar>
        <TitleBlock>
          <Title variant="heading5Small" component="h1">
            {title}
          </Title>
          {subtitle ? (
            <Subtitle variant="subtitle2Small" component="p">
              {subtitle}
            </Subtitle>
          ) : null}
        </TitleBlock>

        {hasRefresh || actions ? (
          <ActionsBlock>
            {hasRefresh ? (
              <>
                {displayedDate ? (
                  <UpdatedText variant="bodyMedium13">
                    {lastUpdatedPrefix} <strong>{formatLastUpdated(displayedDate)}</strong>
                  </UpdatedText>
                ) : null}
                <RefreshButton
                  type="button"
                  aria-label={refreshLabel}
                  data-testid="page-header-refresh"
                  disableRipple
                  disabled={isRefreshing}
                  onClick={() => {
                    void handleRefresh();
                  }}
                  startIcon={
                    isRefreshing ? (
                      <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
                    ) : (
                      <CachedRoundedIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  size="small"
                >
                  {refreshLabel}
                </RefreshButton>
              </>
            ) : null}
            {actions}
          </ActionsBlock>
        ) : null}
      </Bar>
      {children ? <BelowBar data-testid="page-header-extra">{children}</BelowBar> : null}
    </Root>
  );
};

export default PageHeader;
export { PageHeader };

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  width: '100%',
  // Anchor text left: the app sets a global `body { text-align: center }`,
  // which would otherwise centre the (short) title over the subtitle.
  textAlign: 'left',
  paddingBottom: 20,
}));

const Bar = styled('div')(({ theme }) => ({
  display: 'flex',
  // The meta + action sit at the bottom, level with the subtitle (see créa),
  // not next to the title.
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 24,
  width: '100%',
  // RWD: below the small breakpoint the action block drops under the title.
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 16,
  },
}));

// Full-width slot under the bar for page-specific header controls.
const BelowBar = styled('div')(() => ({
  width: '100%',
}));

const TitleBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  minWidth: 0,
  flex: '1 1 auto',
}));

const Title = styled(Typography)(({ theme }) => ({
  // V2 serif heading, but kept in its natural case to match the design.
  textTransform: 'none',
  color: theme.palette.neutral[90],
  margin: 0,
  // Let long titles wrap (and break long/unbreakable words) instead of
  // overflowing the viewport on narrow/mobile screens.
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[60],
  margin: 0,
  maxWidth: 620,
  lineHeight: '24px',
}));

const ActionsBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexShrink: 0,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'space-between',
  },
}));

const UpdatedText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[50],
  whiteSpace: 'nowrap',
  '& strong': {
    fontFamily: '"Poppins SemiBold", sans-serif',
    fontWeight: 600,
    color: theme.palette.neutral[90],
  },
}));

// Sober refresh button matching the V2 charter: white surface, light border,
// understated next to the page's primary actions.
const RefreshButton = styled(MuiButton)(({ theme }) => ({
  textTransform: 'none',
  fontFamily: '"Poppins Medium", sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.neutral[80],
  backgroundColor: theme.palette.neutral.white,
  border: `1px solid ${theme.palette.neutral[20]}`,
  borderRadius: 10,
  padding: '9px 16px',
  height: 40,
  minWidth: 0,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: 8,
  },
  '&:hover': {
    backgroundColor: theme.palette.neutral[10],
    borderColor: theme.palette.neutral[30],
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: theme.palette.neutral[40],
    backgroundColor: theme.palette.neutral.white,
    borderColor: theme.palette.neutral[15],
  },
}));
